"use client";

import {NeoModal} from "@/components/@modal/neo.modal";
import {useNeoContext} from "@/context/neo/neo.context";
import {formatDateTime, formatTime} from "@/libs/helper";
import {vehicleService} from "@/service/vehicle.service";
import {ManageBookingItem} from "@/types/booking";
import {
  GoogleMap,
  Marker,
  Polyline,
  useJsApiLoader
} from "@react-google-maps/api";
import {useEffect, useMemo, useState} from "react";
import {
  FiAlertTriangle,
  FiCalendar,
  FiChevronDown,
  FiChevronUp,
  FiEdit2,
  FiMapPin,
  FiRadio,
  FiUser,
  FiUsers
} from "react-icons/fi";
import {PiBusLight} from "react-icons/pi";
import {BookingUiDetail, getBookingUiDetail} from "./manageBooking.shared";

type BookingVehicleDriverUpdate = Pick<
  ManageBookingItem,
  "vehicleName" | "driverName" | "driverEmpId"
>;

type EditVehicleDriverModalContentProps = {
  booking: ManageBookingItem;
  onSave: (updates: BookingVehicleDriverUpdate) => void;
  onClose: () => void;
};

type SimpleDropdownOption = {
  value: string;
  label: string;
};

const SimpleDropdown = ({
  value,
  options,
  onChange
}: {
  value: string;
  options: SimpleDropdownOption[];
  onChange: (value: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selected = options.find(option => option.value === value);

  return (
    <div className="relative">
      <button
        type="button"
        className="flex h-10 w-full items-center justify-between rounded-lg border border-slate-200 px-2.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-300"
        onClick={() => setIsOpen(prev => !prev)}
      >
        <span className="truncate">{selected?.label ?? "Select"}</span>
        {isOpen ? (
          <FiChevronUp className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        ) : (
          <FiChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[70px] overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
            {options.map(option => (
              <button
                key={option.value}
                type="button"
                className={`block w-full truncate px-2.5 py-1.5 text-left text-xs hover:bg-slate-50 ${
                  option.value === value
                    ? "bg-slate-100 font-semibold text-slate-900"
                    : "text-slate-700"
                }`}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const EditVehicleDriverModalContent = ({
  booking,
  onSave,
  onClose
}: EditVehicleDriverModalContentProps) => {
  const {vehicles, filterContext} = useNeoContext();
  const [vehicleName, setVehicleName] = useState(booking.vehicleName);
  const [driverName, setDriverName] = useState(booking.driverName);
  const [driverEmpId, setDriverEmpId] = useState(booking.driverEmpId);
  const [drivers, setDrivers] = useState<{id: string; name: string}[]>([]);

  const vehicleOptions = useMemo(() => {
    const names = (vehicles || []).map(vehicle => vehicle.trackerName);
    return names.includes(booking.vehicleName)
      ? names
      : [booking.vehicleName, ...names];
  }, [vehicles, booking.vehicleName]);

  useEffect(() => {
    const institutionUserId = filterContext?.client?.institutionUserId;

    if (!institutionUserId) {
      return;
    }

    vehicleService
      .onDriverlists(institutionUserId)
      .then((response: any) => {
        const driverData = response?.data?.data?.driverData;

        if (!driverData?.length) {
          return;
        }

        setDrivers(
          driverData.map((driver: any) => ({
            id: String(driver.userId),
            name: driver.name
          }))
        );
      })
      .catch(() => {
        setDrivers([]);
      });
  }, [filterContext?.client?.institutionUserId]);

  const driverOptions = useMemo(() => {
    const hasCurrentDriver = drivers.some(
      driver => driver.id === booking.driverEmpId
    );

    return hasCurrentDriver
      ? drivers
      : [{id: booking.driverEmpId, name: booking.driverName}, ...drivers];
  }, [drivers, booking.driverEmpId, booking.driverName]);

  return (
    <div className="flex w-[230px] max-w-full flex-col gap-3 p-4">
      <div className="text-sm font-semibold text-slate-800">
        Edit Vehicle & Driver
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-slate-500">Vehicle</label>
        <SimpleDropdown
          value={vehicleName}
          options={vehicleOptions.map(name => ({value: name, label: name}))}
          onChange={setVehicleName}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-slate-500">
          Driver Name ( Driver Code )
        </label>
        <SimpleDropdown
          value={driverEmpId}
          options={driverOptions.map(driver => ({
            value: driver.id,
            label: `${driver.name} (${driver.id})`
          }))}
          onChange={selectedId => {
            const selected = driverOptions.find(
              driver => driver.id === selectedId
            );
            if (selected) {
              setDriverName(selected.name);
              setDriverEmpId(selected.id);
            }
          }}
        />
      </div>

      <div className="mt-1 flex justify-end gap-2">
        <button
          type="button"
          className="rounded-[14px] border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 transition-colors hover:bg-slate-50"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="rounded-[14px] bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-slate-800"
          onClick={() => {
            onSave({vehicleName, driverName, driverEmpId});
            onClose();
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
};

type CancelBookingModalContentProps = {
  vehicleName: string;
  onConfirm: (comment: string) => void;
  onClose: () => void;
};

export const CancelBookingModalContent = ({
  vehicleName,
  onConfirm,
  onClose
}: CancelBookingModalContentProps) => {
  const [comment, setComment] = useState("");
  const isValid = comment.trim().length >= 6;

  return (
    <div className="flex  max-w-full flex-col items-center gap-4 p-4 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-rose-50">
        <FiAlertTriangle className="h-6 w-6 text-rose-600" />
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="text-base font-semibold text-slate-900">
          Cancel this booking?
        </div>
        <div className="text-sm text-slate-700">This cannot be undone.</div>
      </div>

      <div className="w-full text-left">
        <textarea
          className="h-20 w-full resize-none rounded-xl border border-slate-200 p-2.5 text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-gray-700"
          placeholder="Enter a reason for cancelling..."
          value={comment}
          onChange={event => setComment(event.target.value)}
        />
        <div className="mt-1 text-[11px] text-slate-400">
          Minimum 6 characters required
        </div>
      </div>

      <div className="mt-1 flex w-full gap-2">
        <button
          type="button"
          className="flex-1 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
          onClick={onClose}
        >
          No, keep it
        </button>
        <button
          type="button"
          className="flex-1 rounded-full bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors enabled:hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-40"
          disabled={!isValid}
          onClick={() => {
            if (!isValid) {
              return;
            }
            onConfirm(comment.trim());
            onClose();
          }}
        >
          Yes, cancel
        </button>
      </div>
    </div>
  );
};

type BookingMapModalContentProps = {
  booking: ManageBookingItem;
  routeName: string;
  mode: "live" | "map";
  uiDetail: BookingUiDetail;
  statusType?: number;
};

const MAP_DEFAULT_CENTER = {lat: 11.0168, lng: 76.9558};

const getBookingMapPoints = (booking: ManageBookingItem) => {
  const seed = Number(booking.bookingId || 0);
  const baseLat = MAP_DEFAULT_CENTER.lat + ((seed % 7) - 3) * 0.01;
  const baseLng = MAP_DEFAULT_CENTER.lng + ((seed % 5) - 2) * 0.012;

  return {
    start: {lat: baseLat, lng: baseLng},
    end: {
      lat: baseLat + 0.028,
      lng: baseLng + 0.034
    },
    path: [
      {lat: baseLat, lng: baseLng},
      {lat: baseLat + 0.008, lng: baseLng + 0.009},
      {lat: baseLat + 0.015, lng: baseLng + 0.018},
      {lat: baseLat + 0.021, lng: baseLng + 0.026},
      {lat: baseLat + 0.028, lng: baseLng + 0.034}
    ]
  };
};

const getLiveTelemetry = (booking: ManageBookingItem) => {
  const seed = Number(booking.bookingId || 0);
  const totalDistanceKm = 30 + (seed % 60) + ((seed * 7) % 100) / 100;
  const coveredFraction = 0.3 + (seed % 50) / 100;
  const coveredDistanceKm = totalDistanceKm * coveredFraction;
  const isOffline = seed % 9 === 0;
  const speedKph = isOffline ? 0 : 5 + (seed % 55);
  const status = isOffline ? "Offline" : speedKph > 8 ? "Moving" : "Stationary";

  return {
    totalDistanceKm: totalDistanceKm.toFixed(2),
    coveredDistanceKm: coveredDistanceKm.toFixed(2),
    speedKph,
    status
  };
};

const getTripStatus = (
  booking: ManageBookingItem,
  uiDetail: BookingUiDetail
) => {
  const seed = Number(booking.bookingId || 0);
  const noOfStops = 15 + (seed % 20);
  const stopsCovered = Math.min(noOfStops, 5 + (seed % noOfStops));
  const tripType = seed % 2 === 0 ? "Pickup Trip" : "Drop Trip";

  return {
    noOfStops,
    stopsCovered,
    tripType,
    userCounts: uiDetail.seatsFilled,
    tripWindow: {
      from: booking.bookingTime,
      to: booking.bookingTime + 136 * 60 * 1000
    }
  };
};

export const BookingMapModalContent = ({
  booking,
  routeName,
  mode,
  uiDetail,
  statusType
}: BookingMapModalContentProps) => {
  const mapKey: string = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "";
  const {isLoaded} = useJsApiLoader({googleMapsApiKey: mapKey});
  const [isTripStatusExpanded, setIsTripStatusExpanded] = useState(true);
  const mapPoints = useMemo(() => getBookingMapPoints(booking), [booking]);
  const telemetry = useMemo(() => getLiveTelemetry(booking), [booking]);
  const tripStatus = useMemo(
    () => getTripStatus(booking, uiDetail),
    [booking, uiDetail]
  );
  const isCompleted = statusType === 3;
  const hasDistancePanel = mode === "live" || isCompleted;

  const statusColorClass =
    telemetry.status === "Moving"
      ? "text-emerald-600"
      : telemetry.status === "Stationary"
        ? "text-amber-600"
        : "text-rose-600";

  const liveStatusCard = isTripStatusExpanded ? (
    <div className="mt-3 border-t border-slate-200 pt-3">
      {hasDistancePanel && (
        <>
          <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 text-xs text-slate-600">
            <div className="col-span-2 flex items-start gap-1.5">
              <FiMapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
              <div className="flex flex-col">
                <span className="text-[12px] text-slate-400">
                  Total Distance / Possible Distance
                </span>
                <span className="text-xs font-semibold text-slate-700">
                  {telemetry.totalDistanceKm} kms
                </span>
              </div>
            </div>
            <div className="flex items-start gap-1.5">
              <FiMapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
              <div className="flex flex-col">
                <span className="text-[12px] text-slate-400">Covered</span>
                <span className="text-xs font-semibold text-slate-700">
                  {telemetry.coveredDistanceKm} kms
                </span>
              </div>
            </div>
            {mode === "live" && (
              <>
                <div className="flex items-start gap-1.5">
                  <PiBusLight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                  <div className="flex flex-col">
                    <span className="text-[12px] text-slate-400">Speed</span>
                    <span className="text-xs font-semibold text-slate-700">
                      {telemetry.speedKph} kph
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-1.5">
                  <FiRadio
                    className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${statusColorClass}`}
                  />
                  <div className="flex flex-col">
                    <span className="text-[12px] text-slate-400">Status</span>
                    <span
                      className={`text-xs font-semibold ${statusColorClass}`}
                    >
                      {telemetry.status}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="my-3 border-t border-slate-200" />
        </>
      )}

      {hasDistancePanel ? (
        <div className="flex flex-col gap-2.5 text-xs text-slate-600">
          <div className="flex flex-row items-start gap-6">
            <div className="flex items-start gap-1.5">
              <FiMapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
              <div className="flex flex-col">
                <span className="text-[12px] text-slate-400">
                  Stops Covered
                </span>
                <span className="text-xs font-semibold text-slate-700">
                  {tripStatus.stopsCovered}
                </span>
              </div>
            </div>
            <div className="flex items-start gap-1.5">
              <FiUsers className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
              <div className="flex flex-col">
                <span className="text-[12px] text-slate-400">
                  User Counts
                </span>
                <span className="text-xs font-semibold text-slate-700">
                  {tripStatus.userCounts}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-start gap-1.5">
            <PiBusLight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
            <div className="flex flex-col">
              <span className="text-[12px] text-slate-400">No of Stops</span>
              <span className="text-xs font-semibold text-slate-700">
                {tripStatus.noOfStops}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 text-xs text-slate-600">
          <div className="col-span-2 flex items-start gap-1.5">
            <FiMapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
            <div className="flex flex-col">
              <span className="text-[12px] text-slate-400">
                Total Distance / Possible Distance
              </span>
              <span className="text-xs font-semibold text-slate-700">
                {telemetry.totalDistanceKm} kms
              </span>
            </div>
          </div>
          <div className="flex items-start gap-1.5">
            <FiUsers className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
            <div className="flex flex-col">
              <span className="text-[12px] text-slate-400">User Counts</span>
              <span className="text-xs font-semibold text-slate-700">
                {tripStatus.userCounts}
              </span>
            </div>
          </div>
          <div className="flex items-start gap-1.5">
            <PiBusLight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
            <div className="flex flex-col">
              <span className="text-[12px] text-slate-400">No of Stops</span>
              <span className="text-xs font-semibold text-slate-700">
                {tripStatus.noOfStops}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  ) : null;

  const mapCard = (
    <div className="flex h-full flex-col">
      {isLoaded ? (
        <div className="flex-1 overflow-hidden">
          <GoogleMap
            mapContainerStyle={{
              width: "100%",
              height: "100%"
            }}
            center={mapPoints.start}
            zoom={13}
            options={{
              fullscreenControl: true,
              fullscreenControlOptions: {
                position: window.google.maps.ControlPosition.LEFT_BOTTOM
              },
              streetViewControl: false,
              mapTypeControl: false
            }}
          >
            <Polyline
              path={mapPoints.path}
              options={{
                strokeColor: mode === "live" ? "#059669" : "#2563EB",
                strokeOpacity: 0.9,
                strokeWeight: 4
              }}
            />

            <Marker
              position={mapPoints.start}
              title={booking.pickupPoint}
              label={{
                text: "S",
                color: "#ffffff",
                fontSize: "12px",
                fontWeight: "700"
              }}
            />

            {mapPoints.path.slice(1, -1).map((point, index) => (
              <Marker
                key={index}
                position={point}
                title={`Boarding Point ${index + 1}`}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 6,
                  fillColor: "#2563EB",
                  fillOpacity: 1,
                  strokeColor: "#ffffff",
                  strokeWeight: 2
                }}
              />
            ))}

            <Marker
              position={mapPoints.end}
              title={booking.dropPoint}
              label={{
                text: "E",
                color: "#ffffff",
                fontSize: "12px",
                fontWeight: "700"
              }}
            />
          </GoogleMap>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500">
          Google map is loading...
        </div>
      )}
    </div>
  );

  return (
    <div className="isolate flex h-[92vh] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="relative flex-1 overflow-hidden">
        <div className="h-full w-full">{mapCard}</div>

        <div className="absolute left-8 top-8 z-10 flex max-h-[calc(100%-4rem)] w-[280px] flex-col overflow-auto rounded-2xl border border-slate-200 bg-white p-4 shadow-lg">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            {mode === "live" ? "Live Tracking" : "View on Map"}
          </div>
          <button
            type="button"
            className="mt-1 flex items-start justify-between gap-2 text-left"
            onClick={() => setIsTripStatusExpanded(prev => !prev)}
          >
            <div>
              <div className="font-semibold text-slate-800">
                {booking.vehicleName} - {routeName}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {booking.pickupPoint} {"->"} {booking.dropPoint}
              </div>
              <div className="mt-1 text-xs font-semibold text-slate-700">
                {formatTime(tripStatus.tripWindow.from)} -{" "}
                {formatTime(tripStatus.tripWindow.to)}
              </div>
            </div>
            {isTripStatusExpanded ? (
              <FiChevronUp className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
            ) : (
              <FiChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
            )}
          </button>
          {liveStatusCard}
        </div>
      </div>
    </div>
  );
};

export const Card = ({
  b,
  statusType,
  onUpdate,
  onCancel
}: {
  b: ManageBookingItem;
  statusType?: number;
  onUpdate?: (bookingId: number, updates: BookingVehicleDriverUpdate) => void;
  onCancel?: (bookingId: number, comment: string) => void;
}) => {
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const uiDetail = useMemo(
    () => getBookingUiDetail(b, statusType),
    [b, statusType]
  );
  const isOngoing = statusType === 1;
  const isUpcoming = statusType === 2;
  const seatFillPercentage = Math.min(
    100,
    Math.round((uiDetail.seatsFilled / uiDetail.totalSeats) * 100)
  );
  const mapModalMode = isOngoing ? "live" : "map";

  return (
    <>
      <div className="relative h-64 w-full min-w-0 rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_14px_40px_rgba(15,23,42,0.08)] transition-shadow hover:shadow-[0_18px_48px_rgba(15,23,42,0.12)]">
        <div className="flex flex-col gap-2">
          {isUpcoming ? (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-1.5">
                  <div className="whitespace-nowrap font-bold leading-none tracking-tight text-slate-900">
                    {b.vehicleName}
                  </div>
                  {onUpdate && (
                    <button
                      type="button"
                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                      onClick={() => setIsEditOpen(true)}
                      aria-label="Edit vehicle"
                    >
                      <FiEdit2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {onCancel && (
                  <button
                    type="button"
                    className="shrink-0 rounded-[14px] border border-rose-200 bg-white px-3 py-1 text-center text-[11px] font-semibold text-rose-600 transition-colors hover:bg-rose-50"
                    onClick={() => setIsCancelOpen(true)}
                  >
                    Cancel Booking
                  </button>
                )}
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="truncate text-xs font-medium text-slate-500">
                  {uiDetail.routeName}
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <div className="whitespace-nowrap text-xs font-medium text-slate-500">
                    <span className="font-semibold text-slate-700">
                      {uiDetail.seatsFilled}/{uiDetail.totalSeats}
                    </span>{" "}
                    Seats Filled
                  </div>
                  <div className="h-1.5 w-10 shrink-0 overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full bg-emerald-500"
                      style={{width: `${seatFillPercentage}%`}}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
              <div className="min-w-0">
                <div className="truncate font-bold leading-none tracking-tight text-slate-900">
                  {b.vehicleName}
                </div>
                <div className="mt-1 truncate text-xs font-medium text-slate-500">
                  {uiDetail.routeName}
                </div>
              </div>

              <div className="w-32 shrink-0">
                <div className="mt-2 text-right text-xs font-medium text-slate-500">
                  <span className="font-semibold text-slate-700">
                    {uiDetail.seatsFilled}/{uiDetail.totalSeats}
                  </span>{" "}
                  Seats Filled
                </div>
                <div className="mt-1.5 flex justify-end">
                  <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full bg-emerald-500"
                      style={{width: `${seatFillPercentage}%`}}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="rounded-[18px] bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-900">
            {formatTime(b.bookingTime)} -{" "}
            {formatTime(b.bookingTime + 90 * 60 * 1000)}
          </div>

          <div
            className={`relative space-y-2 pl-9 ${isOngoing || isUpcoming ? "-mt-2" : ""}`}
          >
            <div className="absolute left-[11px] top-5 h-[30px] w-0.5 border-l-2 border-dotted border-slate-200" />
            <div className="relative">
              <span className="absolute -left-9 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-50">
                <FiMapPin className="h-3 w-3 text-blue-500" />
              </span>
              <div className="truncate text-sm font-medium text-slate-900">
                {b.pickupPoint}
              </div>
            </div>
            <div className="relative">
              <span className="absolute -left-9 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-50">
                <FiMapPin className="h-3 w-3 text-emerald-500" />
              </span>
              <div className="truncate text-sm font-medium text-slate-900">
                {b.dropPoint}
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-2.5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-500">
              <div className="flex items-center gap-2">
                <FiCalendar className="h-3.5 w-3.5 text-slate-500" />
                <span>{formatDateTime(b.bookingTime).split(",")[0]}</span>
              </div>
              <div className="flex items-center gap-2">
                <FiUser className="h-3.5 w-3.5 text-slate-500" />
                <span>{b.driverName}</span>
                {isUpcoming && onUpdate && (
                  <button
                    type="button"
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                    onClick={() => setIsEditOpen(true)}
                    aria-label="Edit driver"
                  >
                    <FiEdit2 className="h-3 w-3" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <PiBusLight className="h-3.5 w-3.5 text-slate-500" />
                <span>{b.driverEmpId}</span>
              </div>
            </div>
          </div>

          <div className="grid gap-2 xl:grid-cols-2">
            {isOngoing ? (
              <button
                className={`rounded-[18px] px-2 py-2 text-xs font-semibold text-white transition-colors ${
                  uiDetail.liveTrackingAvailable
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : "cursor-not-allowed bg-slate-400"
                }`}
                disabled={!uiDetail.liveTrackingAvailable}
                onClick={() => setIsMapOpen(true)}
                type="button"
              >
                Track Live
              </button>
            ) : (
              <button
                className="rounded-[18px] border border-slate-200 bg-white px-1 py-2.5 text-xs font-semibold text-slate-800 transition-colors hover:bg-slate-50"
                onClick={() => setIsMapOpen(true)}
                type="button"
              >
                View on Map
              </button>
            )}
            <button
              className="rounded-[18px] border border-slate-200 bg-white px-2 py-2.5 text-center text-xs font-semibold text-slate-800 transition-colors hover:bg-slate-50"
              onClick={() => setIsDetailOpen(true)}
              type="button"
            >
              ViewDetails
            </button>
          </div>
        </div>

        {isEditOpen && isUpcoming && onUpdate && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsEditOpen(false)}
            />
            <div className="absolute right-3 top-14 z-50 rounded-2xl border border-slate-200 bg-white shadow-xl">
              <EditVehicleDriverModalContent
                booking={b}
                onClose={() => setIsEditOpen(false)}
                onSave={updates => onUpdate(b.bookingId, updates)}
              />
            </div>
          </>
        )}
      </div>

      <NeoModal
        actionType="manageBookingDetailView"
        onOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        hasActions={false}
        data={{
          booking: b,
          statusType: statusType ?? 1
        }}
      />

      <NeoModal
        actionType="bookingMap"
        onOpen={isMapOpen}
        onClose={() => setIsMapOpen(false)}
        hasActions={false}
        data={{
          booking: b,
          routeName: uiDetail.routeName,
          mode: mapModalMode,
          uiDetail,
          statusType
        }}
      />

      {isUpcoming && onCancel && (
        <NeoModal
          actionType="cancelBooking"
          onOpen={isCancelOpen}
          onClose={() => setIsCancelOpen(false)}
          hasActions={false}
          data={{
            vehicleName: b.vehicleName,
            onConfirm: (comment: string) => onCancel(b.bookingId, comment)
          }}
        />
      )}
    </>
  );
};
